import React from 'react';
import { ApolloError } from '@apollo/client';
import { IconButton, LinearProgress, Menu, MenuItem } from '@mui/material';

import {
  LoadedContent,
  Pagination,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MoreVertIcon,
} from '@occc-app/design-system';
import { GeneralSurveyFragment, SurveyStatus } from '@occc/graphql';
import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';

import { archivableStatuses } from '@occc-app/routes/Survey/SurveyListPage/constants';
import { chipVariantByStatus, titleByStatus } from '../constants';

type SurveysTableProps = {
  surveys: GeneralSurveyFragment[] | null | undefined;
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  isUserPermittedToEditSurvey: boolean;
  isUserPermittedToArchiveSurvey: boolean;
  onEdit: (item: GeneralSurveyFragment) => void;
  onArchive: (item: GeneralSurveyFragment) => void;
  onViewDetails: (item: GeneralSurveyFragment) => void;
};

export const SurveysTable: React.FC<SurveysTableProps> = ({
  surveys,
  loading,
  error,
  paginationProps,
  isUserPermittedToEditSurvey,
  isUserPermittedToArchiveSurvey,
  onEdit,
  onArchive,
  onViewDetails,
}) => {
  const [actionsForUuid, setActionsForUuid] = React.useState<string | null>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (item: GeneralSurveyFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(item.uuid);
    };

  const handleOptionsMenuClose = React.useCallback(() => {
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const menuItemOnClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    doAction: () => void,
  ) => {
    event.stopPropagation();
    doAction();
    handleOptionsMenuClose();
  };

  const renderMoreOptionsMenu = (item: GeneralSurveyFragment) => {
    const shouldRenderEditOption =
      isUserPermittedToEditSurvey && item.status !== SurveyStatus.Archived;
    const shouldRenderArchiveOption =
      archivableStatuses.includes(item.status) && isUserPermittedToArchiveSurvey;
    // TODO: menu with 3 dots is already used in multiple places and
    // it's a good candidate to be a separate component
    return (
      <Menu
        open={item.uuid === actionsForUuid}
        onClose={(event) => {
          (event as any).stopPropagation();
          handleOptionsMenuClose();
        }}
        anchorEl={anchorEl}
        PaperProps={{
          sx: {
            borderRadius: '4px',
          },
        }}
      >
        <MenuItem
          onClick={(event) =>
            menuItemOnClick(event, () => {
              onViewDetails(item);
            })
          }
        >
          View Details
        </MenuItem>

        {shouldRenderEditOption && (
          <MenuItem
            onClick={(event) =>
              menuItemOnClick(event, () => {
                onEdit(item);
              })
            }
          >
            Edit Survey
          </MenuItem>
        )}

        {shouldRenderArchiveOption && (
          <MenuItem
            onClick={(event) =>
              menuItemOnClick(event, () => {
                onArchive(item);
              })
            }
          >
            Archive Survey
          </MenuItem>
        )}
      </Menu>
    );
  };

  const renderData = (items: GeneralSurveyFragment[]) => {
    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Survey Name</TableCell>
              <TableCell>Question</TableCell>
              <TableCell>Status</TableCell>
              {/* table cell for three dots */}
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.uuid}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewDetails(item);
                }}
              >
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.questions[0]?.questionText}</TableCell>
                <TableCell>
                  <StatusChip
                    label={titleByStatus[item.status]}
                    variant={chipVariantByStatus[item.status]}
                  />
                </TableCell>
                <TableCell align='right'>
                  <IconButton onClick={getHandlerForAdditionalActions(item)} sx={{ m: -1 }}>
                    <MoreVertIcon />
                  </IconButton>
                  {renderMoreOptionsMenu(item)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<GeneralSurveyFragment>
      error={error}
      loading={loading}
      items={surveys}
      renderData={renderData}
    />
  );
};
